import React, { useState, useEffect, useContext } from "react";
import NavigationBar from "../components/Navigation";
import { OnlineUsersContext } from "../OnlineUsersContext";
import Footer from "../components/Footer";
import { useParams, useNavigate } from "react-router-dom";
import {
    doc,
    getDoc,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/firebasedeconfig";
import { UserContext } from "../UserContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/components/EditReply.scss";
import { ToastContainer } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

// Le Forum conserve le HTML ; la fiche et le profil utilisent le texte de l'avis.
const extractMovieReviewText = (htmlContent = "") => {
    const parsed = new DOMParser().parseFromString(
        String(htmlContent),
        "text/html"
    );

    parsed.querySelectorAll("script, style").forEach((node) => node.remove());

    // Retirer seulement les deux en-têtes générés.
    const [ratingHeader, filmHeader] = Array.from(parsed.body.children);

    if (
        /^Note MovieFrenzy\s*:/i.test(
            ratingHeader?.textContent.trim() || ""
        ) &&
        /^Film\s*:/i.test(filmHeader?.textContent.trim() || "")
    ) {
        ratingHeader.remove();
        filmHeader.remove();
    }

    // Conserver les paragraphes, listes, sauts de ligne et emojis de Quill.
    const readText = (node) => {
        if (node.nodeType === 3) return node.textContent;
        if (node.nodeType !== 1) return "";
        if (node.tagName === "BR") return "\n";

        const text = Array.from(node.childNodes).map(readText).join("");

        return /^(P|DIV|H[1-6]|LI|UL|OL|BLOCKQUOTE|PRE|TR)$/.test(node.tagName)
            ? `${text}\n\n`
            : text;
    };

    return readText(parsed.body)
        .replace(/\u00a0/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
};

const createMovieReviewExcerpt = (reviewText = "", maxLength = 180) => {
    const text = String(reviewText).replace(/\s+/g, " ").trim();
    const characters = Array.from(text);

    return characters.length <= maxLength
        ? text
        : `${characters.slice(0, maxLength - 1).join("").trim()}…`;
};
const EditReply = () => {
    const { t } = useLanguage();
    const { postId, replyId } = useParams();
    const navigate = useNavigate();
    const [replyContent, setReplyContent] = useState("");
    const [loadedReplyContent, setLoadedReplyContent] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const { userId } = useContext(UserContext);
    const { onlineUsers } = useContext(OnlineUsersContext);

    useEffect(() => {
        if (!userId) return;

        let isActive = true;
        setLoadedReplyContent(null);

        const fetchReply = async () => {
            try {
                const replyRef = doc(db, "posts", postId, "replies", replyId);
                const replySnap = await getDoc(replyRef);

                if (!isActive) return;

                if (replySnap.exists()) {
                    const data = replySnap.data();

                    if (data.authorId !== userId) {
                        toast.error(t("forum.editReply.errorUnauthorized"));
                        navigate(-1);
                    } else {
                        setReplyContent(data.content || "");
                        setLoadedReplyContent(data.content || "");
                    }
                } else {
                    toast.error(t("forum.editReply.errorNotFound"));
                    navigate(-1);
                }
            } catch (error) {
                if (!isActive) return;

                console.error(
                    "Erreur lors de la récupération de la réponse :",
                    error
                );
                toast.error(t("forum.editReply.errorFetch"));
                navigate(-1);
            }
        };

        fetchReply();

        return () => {
            isActive = false;
        };
    }, [postId, replyId, userId, navigate]);

    // Modules pour ReactQuill
    const modules = {
        toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean'],
        ],
    };


    const handleUpdateReply = async () => {
        if (isSaving || loadedReplyContent === null) return;

        if (!userId) {
            toast.error(t("forum.editReply.errorUnauthorized"));
            return;
        }

        setIsSaving(true);

        try {
            const replyRef = doc(db, "posts", postId, "replies", replyId);
            const editedContent = replyContent;

            // Enregistrer ensemble les copies de l'avis.
            // Aucune modification des XP, compteurs, notes ou visibilité.
            await runTransaction(db, async (transaction) => {
                const replySnap = await transaction.get(replyRef);

                if (!replySnap.exists()) {
                    throw new Error(
                        "La réponse a été supprimée. Recharge la discussion."
                    );
                }

                const replyData = replySnap.data();

                if (replyData.authorId !== userId) {
                    throw new Error(
                        "Seul l'auteur peut modifier cette réponse."
                    );
                }

                if ((replyData.content || "") !== loadedReplyContent) {
                    throw new Error(
                        "La réponse a changé depuis son ouverture. Recharge l'éditeur."
                    );
                }

                if (replyData.source === "movie_rating_review") {
                    const movieRatingKey = String(
                        replyData.movieRatingKey || replyData.tmdbId || ""
                    );

                    if (!movieRatingKey || movieRatingKey.includes("/")) {
                        throw new Error(
                            "Identifiant du film manquant ou invalide."
                        );
                    }

                    const userRatingRef = doc(
                        db,
                        "movieRatings",
                        movieRatingKey,
                        "userRatings",
                        userId
                    );

                    const profileReviewRef = doc(
                        db,
                        "users",
                        userId,
                        "movieReviews",
                        movieRatingKey
                    );

                    const ratingSnap = await transaction.get(userRatingRef);
                    const profileSnap = await transaction.get(profileReviewRef);

                    // Éviter de recréer un avis supprimé ou d'écraser un autre avis.
                    if (!ratingSnap.exists() || !profileSnap.exists()) {
                        throw new Error(
                            "Une copie liée de l'avis est absente. Aucune modification enregistrée."
                        );
                    }

                    const ratingData = ratingSnap.data();
                    const profileData = profileSnap.data();

                    if (
                        ratingData.hasReview !== true ||
                        ratingData.forumPostId !== postId ||
                        ratingData.forumReplyId !== replyId ||
                        profileData.forumPostId !== postId ||
                        profileData.forumReplyId !== replyId
                    ) {
                        throw new Error(
                            "Les liens de l'avis ont changé. Recharge la fiche du film."
                        );
                    }

                    const reviewText = extractMovieReviewText(editedContent);

                    if (reviewText.length < 3) {
                        throw new Error(
                            "Le texte de l'avis doit contenir au moins trois caractères."
                        );
                    }

                    transaction.update(userRatingRef, {
                        reviewText,
                        reviewUpdatedAt: serverTimestamp(),
                    });

                    transaction.update(profileReviewRef, {
                        reviewText,
                        reviewExcerpt: createMovieReviewExcerpt(reviewText),
                        updatedAt: serverTimestamp(),
                    });
                }

                // Une réponse ordinaire ne modifie que son propre document.
                transaction.update(replyRef, {
                    content: editedContent,
                    updatedAt: serverTimestamp(),
                });
            });

            toast.success(t("forum.editReply.successUpdate"));
            navigate(`/subject/${postId}?replyId=${replyId}`);
        } catch (error) {
            console.error("Erreur lors de la mise à jour :", error);
            toast.error(t("forum.editReply.failUpdate"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <div className="edit-reply-page"
                style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                    backgroundBlendMode: "overlay",
                    backgroundColor: "#200211",
                    opacity: "0.9",
                    overflowX: "hidden",
                    overflowY: "auto",
                    minHeight: "100vh",
                }}
            >
                <NavigationBar />
                <div className="edit-reply-container"
                    style={{
                        marginBottom: "4.5rem",
                        marginTop: "4.5rem",
                        padding: "1.5rem",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "0.5rem",
                        boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.5)",
                        border: "2px solid #00FFFF",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: "100%",
                        minHeight: "100%",
                    }}
                >
                    <h2>{t('forum.editReply.pageTitle')}</h2>
                    <ReactQuill
                        theme="snow"
                        value={replyContent}
                        readOnly={isSaving || loadedReplyContent === null}
                        onChange={setReplyContent}
                        modules={modules}
                        placeholder={t('forum.editTopic.contentPlaceholder')}
                        className="quill-editor"
                        style={{ width: "100%", height: "100%", minHeight: "50vh !important" }}
                    />
                    <br />
                    <div className="button-container"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "1rem",
                            height: "100%",

                        }}>
                        <button
                            onClick={handleUpdateReply}
                            disabled={isSaving || loadedReplyContent === null}
                            className="saveEditReply-btn"
                        >
                            {t("forum.editReply.save")}
                        </button>

                        <button
                            onClick={() => navigate(-1)}
                            disabled={isSaving}
                            className="cancelEditReply-btn"
                        >
                            {t("forum.editReply.cancel")}
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        </>


    );
};

export default EditReply;
